import { useEffect, useId, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback": () => void;
          "error-callback": () => void;
          theme: "light";
          language: string;
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      "script[data-workingdog-turnstile]",
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("TURNSTILE_SCRIPT_FAILED")), {
        once: true,
      });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.workingdogTurnstile = "true";
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("TURNSTILE_SCRIPT_FAILED")), {
      once: true,
    });
    document.head.append(script);
  });
  return scriptPromise;
}

export function TurnstileWidget({
  siteKey,
  required,
  onToken,
  onError,
}: {
  siteKey: string;
  required: boolean;
  onToken: (token: string) => void;
  onError: (message: string) => void;
}) {
  const id = useId();
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!required || !siteKey || !container.current) return;
    let widgetId: string | null = null;
    let cancelled = false;
    void loadTurnstile()
      .then(() => {
        if (cancelled || !container.current || !window.turnstile) return;
        widgetId = window.turnstile.render(container.current, {
          sitekey: siteKey,
          callback: onToken,
          "expired-callback": () => onToken(""),
          "error-callback": () => onError("Không thể tải xác minh chống bot."),
          theme: "light",
          language: "vi",
        });
      })
      .catch(() => onError("Không thể tải xác minh chống bot."));
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onError, onToken, required, siteKey]);

  if (!required) return null;
  if (!siteKey) {
    return <p className="form-alert form-alert--error">Turnstile chưa được cấu hình.</p>;
  }
  return <div id={id} ref={container} className="turnstile-slot" />;
}
